import { Column, ColumnMeta, Row, Table } from '@tanstack/react-table';
import { sum } from 'lodash';
import { Fragment } from 'react';
import styled, { css } from 'styled-components';
import { useIsTeam } from '../../../../../hooks';
import { BouSpacingLevel, fluidSpacing } from '../../../../../layout/bou-spacing';
import { getTextStyle } from '../../../../texts/bou-text/text-style';
import { TableV2DownloadEventHandlers, TableV2FilteringProps, TableV2RenderProps } from '../../types';
import { getClearFiltersAction } from '../../logic/clear-filters';
import { EmptyState, ErrorState } from './empty-state';
import { Paginator, PaginatorProps } from './paginator';
import { TableAreaWrap } from './table-area-wrap';
import { ShowColumnsSelector } from './show-columns-selector';
import { TBody } from './tbody';
import { THead } from './thead';
import { DownloadAsExcel } from './download-as-excel';
import { TFoot } from './tfoot';
import { StateMessage } from '@upright/ui/common/generic/components/state-message';
import { Button } from '@upright/ui/common/generic/components/button';

export function TableV2DefaultRenderer<T extends object, ColumnId extends string = string>(
  props: TableV2RenderProps<T, ColumnId> &
    TableV2DownloadEventHandlers & {
      paginatorProps: PaginatorProps;
      handleTableExport: (() => void) | undefined;
    } & { table: Table<T> } & { onRowClick?: (row: Row<T>) => void } & {
      filtering?: TableV2FilteringProps<ColumnId>;
    },
) {
  const isTeam = useIsTeam();
  const showColumnSelect = props.showColumnSelect === true || (props.showColumnSelect === 'teamOnly' && isTeam);

  // This boolean would be expanded with OR if there are multiple things (buttons, etc) to show above the table
  const showTopRow = showColumnSelect;
  const showVerticalBorders = props.showVerticalBorders ?? false;
  const variant = props.variant ?? 'default';
  const { table, getRowProps, getCellProps, getColumnProps, getHeaderProps, handleTableExport, eventHandlers } = props;

  const visibleCols = table.getVisibleLeafColumns();
  const showFooter = props.showFooter ?? table.getAllColumns().some(c => c.columnDef.footer);
  const clearFilters = getClearFiltersAction(props.filtering, table);

  return (
    <Fragment>
      {(props.isFetchingPageData || props.isLoading) && (
        <progress style={{ display: 'none' }} /> // Semantic element for tests to recognize a loading state
      )}
      {showTopRow && (
        <TopRow>
          {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
          {showColumnSelect && <ShowColumnsSelector columns={table.getAllColumns()} />}
        </TopRow>
      )}
      <TableAreaWrap
        styleOverrides={props.tableAreaStyles}
        overflowFadeDeps={[visibleCols.map(c => c.id).join(''), props.isFetchingPageData]}
        variant={variant}
      >
        <TableElement columns={visibleCols} isFetchingPageData={!!props.isFetchingPageData}>
          <THead
            table={table}
            getHeaderProps={getHeaderProps}
            getColumnProps={getColumnProps}
            eventHandlers={props.eventHandlers}
            data-testid={props['data-testid']}
            showVerticalBorders={showVerticalBorders}
            variant={variant}
          />
          <TBody
            table={table}
            ChildComponent={props.childComponent}
            getRowProps={getRowProps}
            getCellProps={getCellProps}
            getColumnProps={getColumnProps}
            data-testid={props['data-testid']}
            showVerticalBorders={showVerticalBorders}
            onRowClick={props.onRowClick}
          />
          {props.isError ? (
            <ErrorState content={props.errorState} />
          ) : table.getCoreRowModel().rows.length === 0 ? (
            <EmptyState content={props.emptyState} variant={variant} />
          ) : !props.isLoading && !table.options.manualFiltering && table.getFilteredRowModel().rows.length === 0 ? (
            <EmptyState
              variant={variant}
              content={
                <StateMessage
                  layout="section"
                  title="No results match your filters"
                  description={
                    clearFilters
                      ? 'Clear your filters to see the available results.'
                      : 'Change your filters to see the available results.'
                  }
                  announce
                  primaryAction={
                    clearFilters ? (
                      <Button variant="outlined" onPress={clearFilters}>
                        Clear filters
                      </Button>
                    ) : undefined
                  }
                />
              }
            />
          ) : undefined}
          {showFooter && (
            <TFoot table={table} getColumnProps={getColumnProps} showVerticalBorders={showVerticalBorders} />
          )}
        </TableElement>
      </TableAreaWrap>
      <BottomRow>
        {handleTableExport && (
          <DownloadAsExcel
            disabled={props.isLoading || props.isError}
            onClick={() => {
              if (eventHandlers?.onDownloadButtonClick) {
                eventHandlers.onDownloadButtonClick();
              }
              handleTableExport();
            }}
          />
        )}
        {props.paginatorProps.enablePagination && props.disablePaginationControls !== true && (
          <Paginator
            {...{
              ...props.paginatorProps,
              isFetchingPageData: !!props.isFetchingPageData,
              eventHandlers: props.eventHandlers,
              showPageSizeSelector: props.showPageSizeSelector ?? 'never',
              pageSizeOptions: props.pageSizeOptions,
            }}
          />
        )}
      </BottomRow>
    </Fragment>
  );
}

export const Root = styled.div`
  position: relative;
  display: block;
  width: 100%;
  min-height: 0px;
  overflow-y: auto;
  ${getTextStyle({ size: 'xxs' })}
`;

const TableElement = styled.table<{
  columns: Array<Column<any, any>>;
  isFetchingPageData: boolean;
}>`
  ${props =>
    props.isFetchingPageData &&
    css`
      filter: grayscale(100%) opacity(50%);
    `}

  position: relative;
  border-spacing: 0;
  display: grid;
  grid-template-columns: ${props => {
    /**
     * A logic to determine column widths in a flexible way.
     *
     * Widens columns proportionally for large screens, using columnMinWidth as the minimum pixel width.
     * In other words, once the proportionalWidth would be narrower than columnMinWidth in pixels,
     * the column will use columnMinWidth. This will cause the whole table to x-scroll instead of getting smaller.
     *
     * If a string value for width is provided, it is used directly instead of the logic above.
     * NOTE: In this case, any columns that have number/undefined as width treat these string-defined columns
     * as having a width of 150 when determining their own proportional width.
     *
     * If we want to support resizeable columns, the logic should happen somewhere here, based on column.getSize().
     */
    const sumOfMinWidths = sum(props.columns.map(c => getColumnMinWidth(c.columnDef.meta?.width)));
    return props.columns
      .map(c => {
        const widthDefinition = c.columnDef.meta?.width;
        if (typeof widthDefinition === 'string') return widthDefinition;
        const columnMinWidth = getColumnMinWidth(widthDefinition);
        const proportionalWidth = columnMinWidth / sumOfMinWidths;
        return `max(${proportionalWidth * 100}%, ${columnMinWidth}px)`;
      })
      .join(' ');
  }};
`;

function getColumnMinWidth(widthDefinition: ColumnMeta<any, any>['width']) {
  if (typeof widthDefinition === 'number') return widthDefinition;
  return 150;
}

const TopRow = styled.div`
  ${fluidSpacing('padding-bottom', BouSpacingLevel.TINY)};
  display: flex;
  justify-content: flex-start;
  align-items: baseline;
`;
const BottomRow = styled.div`
  margin-top: 1.3em;
  gap: 0.5em;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;
